
import { Card } from '../../../components/atoms/Card'
import { Badge } from '../../../components/atoms/Badge'

interface KPICardsProps {
  systemData: any;
}

export function KPICards({ systemData }: KPICardsProps) {
  if (!systemData) return null;

  const { powerFlow, inverter, meter } = systemData;

  const kpis = [
    {
      title: 'Potencia FV Actual',
      value: powerFlow?.P_PV !== undefined ? `${powerFlow.P_PV.toLocaleString()}` : 'N/A',
      unit: 'W',
      icon: 'ri-sun-line',
      gradient: 'from-yellow-400 to-orange-500',
      bgGradient: 'from-yellow-50 to-orange-50',
      iconBg: 'bg-gradient-to-br from-yellow-400 to-orange-500',
      textColor: 'text-yellow-700',
      borderColor: 'border-yellow-200'
    },
    {
      title: 'Consumo Actual',
      value: powerFlow?.P_Load !== undefined ? `${powerFlow.P_Load.toLocaleString()}` : 'N/A',
      unit: 'W',
      icon: 'ri-home-4-line',
      gradient: 'from-blue-400 to-blue-600',
      bgGradient: 'from-blue-50 to-blue-100',
      iconBg: 'bg-gradient-to-br from-blue-400 to-blue-600',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200'
    },
    {
      title: 'Flujo a Red',
      value: powerFlow?.P_Grid !== undefined ? `${Math.abs(powerFlow.P_Grid).toLocaleString()}` : 'N/A',
      unit: 'W',
      icon: powerFlow?.P_Grid && powerFlow.P_Grid < 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line',
      gradient: powerFlow?.P_Grid && powerFlow.P_Grid < 0 ? 'from-green-400 to-emerald-600' : 'from-red-400 to-red-600',
      bgGradient: powerFlow?.P_Grid && powerFlow.P_Grid < 0 ? 'from-green-50 to-emerald-50' : 'from-red-50 to-red-100',
      iconBg: powerFlow?.P_Grid && powerFlow.P_Grid < 0 ? 'bg-gradient-to-br from-green-400 to-emerald-600' : 'bg-gradient-to-br from-red-400 to-red-600',
      textColor: powerFlow?.P_Grid && powerFlow.P_Grid < 0 ? 'text-green-700' : 'text-red-700',
      borderColor: powerFlow?.P_Grid && powerFlow.P_Grid < 0 ? 'border-green-200' : 'border-red-200',
      subtitle: powerFlow?.P_Grid && powerFlow.P_Grid < 0 ? 'Exportando' : 'Importando'
    },
    {
      title: 'SOC Batería',
      value: powerFlow?.Battery?.SOC !== undefined ? `${powerFlow.Battery.SOC}` : 'N/A',
      unit: '%',
      icon: 'ri-battery-charge-line',
      gradient: 'from-green-400 to-green-600',
      bgGradient: 'from-green-50 to-green-100',
      iconBg: 'bg-gradient-to-br from-green-400 to-green-600',
      textColor: 'text-green-700',
      borderColor: 'border-green-200'
    },
    {
      title: 'Autoconsumo',
      value: powerFlow?.rel_SelfConsumption !== undefined ? `${powerFlow.rel_SelfConsumption}` : 'N/A',
      unit: '%',
      icon: 'ri-recycle-line',
      gradient: 'from-teal-400 to-cyan-600',
      bgGradient: 'from-teal-50 to-cyan-50',
      iconBg: 'bg-gradient-to-br from-teal-400 to-cyan-600',
      textColor: 'text-teal-700',
      borderColor: 'border-teal-200'
    },
    {
      title: 'Autosuficiencia',
      value: powerFlow?.rel_Autonomy !== undefined ? `${powerFlow.rel_Autonomy}` : 'N/A',
      unit: '%',
      icon: 'ri-shield-check-line',
      gradient: 'from-purple-400 to-purple-600',
      bgGradient: 'from-purple-50 to-purple-100',
      iconBg: 'bg-gradient-to-br from-purple-400 to-purple-600',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-200'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {kpis.map((kpi, index) => (
        <Card
          key={index}
          gradient={`bg-gradient-to-br ${kpi.bgGradient}`}
          className={`border-2 ${kpi.borderColor} hover:scale-105 cursor-pointer`}
        >
          <div className="flex items-start justify-between mb-3">
            <div className={`${kpi.iconBg} w-12 h-12 rounded-lg flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-200`}>
              <i className={`${kpi.icon} text-white w-6 h-6 flex items-center justify-center text-2xl`}></i>
            </div>
            {kpi.subtitle && (
              <Badge className={`bg-white/80 ${kpi.textColor}`}>
                {kpi.subtitle}
              </Badge>
            )}
          </div>
          
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            {kpi.title}
          </h3>
          
          <div className="flex items-baseline space-x-1">
            <span className={`text-2xl font-bold ${kpi.textColor}`}>
              {kpi.value}
            </span>
            <span className={`text-sm font-semibold ${kpi.textColor} opacity-80`}>
              {kpi.unit}
            </span>
          </div>

          <div className="mt-3 flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${kpi.textColor.replace('text-', 'bg-')} animate-pulse`}></div>
            <Badge tone="live">En vivo</Badge>
          </div>
        </Card>
      ))}
    </div>
  );
}
